import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadCaptureModal = ({ isOpen, onClose, modalType }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    product_type: '',
    primary_concern: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const productTypes = ['SaaS', 'FinTech', 'E-commerce', 'Other'];
  const concerns = ['Payments', 'Onboarding', 'UX', 'Reliability', 'Other'];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/leads`, formData);
      const leadId = response.data.id;

      if (modalType === 'diagnostic') {
        navigate(`/diagnostic/${leadId}`);
      } else {
        alert('Thank you! We will contact you shortly for a full QA audit.');
        onClose();
      }
    } catch (err) {
      setError('Failed to submit. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getSubmitButtonLabel = () => {
    if (loading) return 'Submitting...';
    if (modalType === 'diagnostic') return 'Start Diagnostic';
    return 'Submit Request';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="glass-card p-8 rounded-lg max-w-md w-full relative"
            data-testid="lead-capture-modal"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="modal-close-button"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-2xl font-display font-bold mb-2" data-testid="modal-title">
              {modalType === 'diagnostic' ? 'Start Your Diagnostic' : 'Request Pre-Release Audit'}
            </h2>
            <p className="text-sm text-muted-foreground mb-6" data-testid="modal-description">
              {modalType === 'diagnostic' 
                ? 'Enter your details to begin the 10-minute Trust Signal Scan'
                : 'Share your details and we\'ll reach out to discuss a comprehensive audit'}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2" data-testid="name-label">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 bg-background border border-input rounded-md focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  data-testid="name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" data-testid="company-label">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 bg-background border border-input rounded-md focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  data-testid="company-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" data-testid="email-label">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 bg-background border border-input rounded-md focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" data-testid="product-type-label">Product Type</label>
                <select
                  name="product_type"
                  value={formData.product_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 bg-background border border-input rounded-md focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  data-testid="product-type-select"
                >
                  <option value="">Select...</option>
                  {productTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" data-testid="concern-label">Primary Concern</label>
                <select
                  name="primary_concern"
                  value={formData.primary_concern}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 bg-background border border-input rounded-md focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  data-testid="concern-select"
                >
                  <option value="">Select...</option>
                  {concerns.map((concern) => (
                    <option key={concern} value={concern}>{concern}</option>
                  ))}
                </select>
              </div>

              {error && (
                <div className="text-sm text-destructive" data-testid="error-message">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-3 bg-[#FF6A00] text-white font-medium rounded-md hover:bg-[#FF6A00]/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-button"
              >
                {getSubmitButtonLabel()}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LeadCaptureModal;
